import { useCallback, useMemo, useState, type ReactNode } from 'react'
import {
  FormRequirementsContext,
  REQUIREMENT_MARK_LABEL,
  requirementMarks,
  type RequirementIndicators,
  type RequirementMark,
} from './requirementMarks'

/**
 * One form's requirement marks. Its fields (FieldInput, FieldRow, or any
 * control using useRequirementMark) count themselves in, and it marks the less
 * common kind: nothing when every field is required or every one is optional,
 * "Optional" on the few optional fields of a mostly required form, "Required"
 * on the few required ones of a mostly optional form. `indicators` forces
 * otherwise. ModalShell, SidePanelShell and the automation editor's side panel
 * provide one; wrap any other form in one. A nested one is a form of its own.
 */
export function FormRequirements({
  indicators = 'auto',
  children,
}: {
  indicators?: RequirementIndicators
  children: ReactNode
}) {
  const [fields, setFields] = useState<ReadonlyMap<string, boolean>>(() => new Map())

  const register = useCallback((id: string, required: boolean) => {
    setFields((current) =>
      current.get(id) === required ? current : new Map(current).set(id, required)
    )
  }, [])
  const unregister = useCallback((id: string) => {
    setFields((current) => {
      if (!current.has(id)) return current
      const next = new Map(current)
      next.delete(id)
      return next
    })
  }, [])

  const required = [...fields.values()].filter(Boolean).length
  const optional = fields.size - required
  const marks = useMemo(
    () => requirementMarks({ required, optional }, indicators),
    [indicators, optional, required]
  )
  const value = useMemo(() => ({ register, unregister, marks }), [marks, register, unregister])

  return (
    <FormRequirementsContext.Provider value={value}>{children}</FormRequirementsContext.Provider>
  )
}

/** A field's requirement mark, drawn the one way: muted italic beside its label. */
export function RequirementMarkLabel({
  mark,
  className = 'text-muted',
}: {
  mark: RequirementMark
  className?: string
}) {
  if (!mark) return null
  return (
    <span
      className={`field-input-requirement inline-flex items-center gap-1 text-xs font-normal italic leading-4 [font-synthesis:style] ${className}`}
    >
      {REQUIREMENT_MARK_LABEL[mark]}
    </span>
  )
}
